import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Drawer } from '@mui/material';
import { IconHelp } from '@tabler/icons-react';

import { Button, Tooltip, Typography } from '@zendesk/forethought-elements';
import networkErrorIcon from 'src/assets/images/icon-error-exclamation.svg';
import Spinner from 'src/components/spinner';
import { getCurrentUser, selectSession } from 'src/slices/session/sessionSlice';
import { useAppDispatch } from 'src/store/hooks';
import {
  isForethoughtWidgetErrorEvent,
  isForethoughtWidgetLoadedEvent,
} from 'src/types/solveWidgetEventTypes';
import { getFullName } from 'src/utils/sessionUtils';

interface ForethoughtWidgetClosedEvent extends MessageEvent {
  data: {
    event: 'forethoughtWidgetClosed';
  };
}

function getKjurJwt() {
  try {
    const jwt = localStorage.getItem('KJUR-jwt');
    const jwtTimestamp = localStorage.getItem('KJUR-jwt-timestamp');
    const jwtAge = Date.now() - Number(jwtTimestamp);
    const jwtAgeLimit = 1000 * 60 * 60 * 24; // 1 day
    const isJwtExpired = jwtAge > jwtAgeLimit;

    if (jwt && !isJwtExpired) {
      return jwt;
    }
  } catch {
    // Continue regardless of error
  }

  return null;
}

export default function WidgetLaunchButton({ color }: { color: string }) {
  const { t } = useTranslation('app');
  const dispatch = useAppDispatch();

  const { currentUser, organization } = useSelector(selectSession);

  useEffect(() => {
    if (!currentUser.data) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, currentUser.data]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const forethoughtWidgetClosedListener = (
      event: ForethoughtWidgetClosedEvent,
    ) => {
      if (event.data.event === 'forethoughtWidgetClosed') {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener('message', forethoughtWidgetClosedListener);

    return () => {
      window.removeEventListener('message', forethoughtWidgetClosedListener);
    };
  }, []);

  // This state is used to switch the Drawer variant from `temporary` to
  // `persistent` after the first click on the "Get Help" button, to lazy load
  // the iframe:
  const [isDrawerInitialized, setIsDrawerInitialized] = useState(false);

  const handleGetHelpButtonClick = useCallback(() => {
    setIsDrawerInitialized(true);
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleErrorDismiss = useCallback(() => {
    setIsDrawerOpen(false);
    setIsDrawerInitialized(false);
    setWidgetState('loading');
  }, []);

  const [widgetState, setWidgetState] = useState<
    'loading' | 'loaded' | 'error'
  >('loading');

  const timeoutIdRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleIframeLoad = useCallback(() => {
    // Set error state if `forethoughtWidgetLoaded` event is not received
    // within 5 seconds of iframe load:
    timeoutIdRef.current = setTimeout(() => {
      setWidgetState('error');
    }, 5000);
  }, []);

  const solveOrigin = new URL(SOLVE_URL).origin;

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.origin === solveOrigin) {
        if (isForethoughtWidgetLoadedEvent(event)) {
          clearTimeout(timeoutIdRef.current);
          setWidgetState('loaded');

          return;
        }

        if (isForethoughtWidgetErrorEvent(event)) {
          clearTimeout(timeoutIdRef.current);
          setWidgetState('error');
        }
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [solveOrigin]);

  if (!currentUser.data) {
    return null;
  }

  const parameters = new URLSearchParams({
    'config-ft-disable-forethought-branding': 'true',
    'config-ft-disable-proactive-prompt': 'true',
    'config-ft-mobile-sdk': 'true',
    'config-ft-widget-article-search-enabled': 'true',
    'config-ft-widget-header-title': 'Forethought Help',
    'data-api-key': '38b2d6ed-4abd-48ad-af0b-0d2d7b871f0c',
    'data-ft-email': currentUser.data.email,
    'data-ft-first-name': currentUser.data.first_name,
    'data-ft-full-name': getFullName(currentUser.data),
    'data-ft-last-name': currentUser.data.last_name,
    'data-ft-widget-location': 'dashboard',
    'full-screen': 'true',
    'position-y': 'top',
    v: '2',

    ...(organization.data?.display_name
      ? { 'data-ft-company-name': organization.data?.display_name }
      : {}),
  });

  try {
    const jwt = getKjurJwt();
    if (jwt) {
      parameters.set('data-ft-data-jwt', jwt);
    }
  } catch {
    // JWT generation failed. Continue regardless of error.
  }

  return (
    <>
      <Tooltip tooltipContent={t('widgetLaunch.getHelp')}>
        <Button
          aria-label={t('widgetLaunch.getHelp')}
          onClick={handleGetHelpButtonClick}
          startIcon={<IconHelp color={color} size={22} />}
          style={{ border: 0, width: '32px' }}
          variant='ghost'
        ></Button>
      </Tooltip>
      <Drawer
        anchor='right'
        hideBackdrop
        onClose={handleDrawerClose}
        open={isDrawerOpen}
        // Use `temporary` variant initially, and switch to `persistent` after
        // the first click on the "Get Help" button.
        // This is essentially lazy loading for the iframe.
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px 0 0 12px',
              boxShadow:
                '0px 0px 1px 0px rgba(0, 12, 32, 0.02), 2px 8px 16px 0px rgba(3, 17, 38, 0.11)',
              width: '480px',
            },
          },
        }}
        variant={isDrawerInitialized ? 'persistent' : 'temporary'}
      >
        <iframe
          id='forethought-chat'
          name='Forethought Help'
          onLoad={handleIframeLoad}
          sandbox='allow-scripts allow-popups allow-top-navigation-by-user-activation allow-same-origin allow-popups-to-escape-sandbox allow-downloads allow-top-navigation'
          src={`${solveOrigin}?${parameters}`}
          style={{
            border: 'none',
            display: widgetState === 'loaded' ? 'block' : 'none',
            height: '100%',
            width: '100%',
          }}
          title={t('navbar.widgetIframeTitle')}
        />
        {widgetState === 'loading' && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </Box>
        )}
        {widgetState === 'error' && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <img alt='' src={networkErrorIcon} width='32px' />
            <Typography variant='font16'>{t('navbar.widgetError')}</Typography>
            <Typography variant='font16'>
              {t('navbar.widgetErrorRetry')}
            </Typography>
            <Button onClick={handleErrorDismiss}>
              {t('navbar.widgetErrorDismiss')}
            </Button>
          </Box>
        )}
      </Drawer>
    </>
  );
}
